import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, Plus } from "lucide-react";
import { Component, type ErrorInfo, type ReactNode } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { bootstrapCustomerAuth } from "@repair/api";
import { AppMark, Button, EmptyState, IconButton, LoadingState, PageBanner } from "@repair/ui";
import { DetailPage } from "./pages/DetailPage";
import { OrdersPage } from "./pages/OrdersPage";
import { SubmitPage } from "./pages/SubmitPage";
import { SuccessPage } from "./pages/SuccessPage";

function Shell({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const isHome = ["/", "/index.html", "/orders", "/orders.html"].includes(location.pathname);
  return (
    <div className="customer-shell">
      <header className="customer-topbar">
        <div className="customer-topbar__inner">
          {isHome ? <AppMark /> : <IconButton label="返回" onClick={() => navigate(-1)}><ChevronLeft size={22} /></IconButton>}
          <nav aria-label="客户服务导航">
            <Button variant="ghost" onClick={() => navigate("/orders")}>我的返修</Button>
            <Button variant="secondary" onClick={() => navigate("/new")}><Plus size={16} />报修</Button>
          </nav>
        </div>
      </header>
      <main>{children}</main>
    </div>
  );
}

function AuthGate({ children }: { children: React.ReactNode }) {
  const auth = useQuery({ queryKey: ["customer-auth"], queryFn: bootstrapCustomerAuth, staleTime: Infinity, retry: false });
  if (auth.isLoading) return <div className="customer-page"><LoadingState label="正在识别微信身份" /></div>;
  if (auth.error) {
    return <div className="customer-page"><PageBanner tone="danger" title="身份识别失败">{auth.error.message}</PageBanner></div>;
  }
  if (!auth.data?.ready) {
    return (
      <div className="customer-page customer-auth-empty">
        <PageBanner tone="attention" title="请从微信公众号进入">当前浏览器没有有效的微信客户会话。</PageBanner>
        <Button onClick={() => window.location.reload()}>重新识别</Button>
      </div>
    );
  }
  return children;
}

function LegacyDetailRedirect() {
  const id = new URLSearchParams(useLocation().search).get("id");
  return id ? <Navigate to={`/orders/${encodeURIComponent(id)}`} replace /> : <Navigate to="/orders" replace />;
}

class CustomerRenderBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("Customer page render failed", error, info);
  }

  render() {
    if (this.state.failed) {
      return (
        <div className="customer-page">
          <EmptyState
            title="页面暂时无法显示"
            detail="工单数据格式异常，请重新读取。"
            action={<Button onClick={() => window.location.reload()}>重新加载</Button>}
          />
        </div>
      );
    }
    return this.props.children;
  }
}

export function CustomerApp() {
  return (
    <AuthGate>
      <Shell>
        <CustomerRenderBoundary>
          <Routes>
            <Route path="/" element={<SubmitPage />} />
            <Route path="/index.html" element={<SubmitPage />} />
            <Route path="/new" element={<SubmitPage />} />
            <Route path="/orders" element={<OrdersPage />} />
            <Route path="/orders.html" element={<OrdersPage />} />
            <Route path="/orders/:id" element={<DetailPage />} />
            <Route path="/order-detail.html" element={<LegacyDetailRedirect />} />
            <Route path="/supplement.html" element={<LegacyDetailRedirect />} />
            <Route path="/confirm-receipt.html" element={<LegacyDetailRedirect />} />
            <Route path="/progress.html" element={<LegacyDetailRedirect />} />
            <Route path="/success" element={<SuccessPage />} />
            <Route path="*" element={<Navigate to="/orders" replace />} />
          </Routes>
        </CustomerRenderBoundary>
      </Shell>
    </AuthGate>
  );
}
